import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/components/auth/AuthProvider';
import useApi from '@/hooks/useApi';
import {
  describeClientContext,
  renderSmartPrompts,
  type SmartPromptItem,
  type SmartPromptsResponse,
} from '@/services/smartPrompts';
import type { SuggestedPrompt } from '@/components/chat/ChatLandingState';

interface UseSmartPromptsOptions {
  /** Hold the request until the caller's own data is settled. */
  enabled?: boolean;
  /** How many prompts to ask for. The landing screen shows three. */
  limit?: number;
  /**
   * The client-held conversation context, for a guest.
   *
   * A guest has no server-side records, so without this the ranking can only
   * offer onboarding prompts. Passing the context this client already builds
   * for chat — a cached address, the ballot fetched for it — gets a guest the
   * same ballot-aware ranking a signed-in voter gets. Ignored by the backend
   * for a signed-in caller, whose own records are authoritative.
   */
  clientContext?: unknown;
  /**
   * Re-reads the ranking when this changes. A signed-in voter's ballot is read
   * server-side, so nothing else in the request moves when their address does;
   * the caller passes a key for the ballot it is showing.
   */
  refreshKey?: string;
}

interface UseSmartPromptsResult {
  /** Rendered chips, or `null` while loading or after a failure. */
  prompts: SuggestedPrompt[] | null;
  /**
   * True only for the brief window before the first response lands, so the
   * caller can hold its chip row and avoid showing defaults that swap a moment
   * later. Capped by {@link MAX_HOLD_MS}: past that the caller renders its
   * fallbacks, because a chip row that is merely late is far better than one
   * that is empty.
   */
  isLoadingFirstResult: boolean;
}

/**
 * How long the caller may hold its chip row waiting for the first response.
 *
 * This is a backstop, not a timeout — the request keeps going and its result
 * still lands when it arrives. It exists so that no failure inside this hook
 * can leave the landing screen with no chips at all.
 */
export const MAX_HOLD_MS = 1500;

/**
 * Fetch this voter's ranked landing prompts.
 *
 * Returns `null` rather than an error state: the landing screen always has a
 * local chip set to fall back to, so a failed or empty fetch reads as "nothing
 * better than the default" to the caller. That keeps the failure mode
 * identical to the behavior before smart prompts existed.
 *
 * The endpoint answers guests too, so this fires regardless of auth state and
 * re-reads when the auth state or the language changes — the ranking depends
 * on both.
 *
 * **The effect is keyed on the request signature alone.** `useApi`'s `query` is
 * rebuilt on every render, so depending on it re-ran this effect constantly;
 * each re-run cancelled the previous request before it could record that it had
 * settled. Holding `query` in a ref keeps the effect keyed to what actually
 * changes the request, so a cancel is always followed by a fetch that settles.
 *
 * Mirrors `votemate-mobile-app/src/hooks/useSmartPrompts.ts`.
 */
export default function useSmartPrompts({
  enabled = true,
  limit = 3,
  clientContext,
  refreshKey = '',
}: UseSmartPromptsOptions = {}): UseSmartPromptsResult {
  const { t, i18n } = useTranslation('chat');
  const query = useApi();
  const { isAuthenticated } = useAuth();
  const language = i18n.language;

  // Held in a ref so the fetch always calls the current `query` without the
  // effect depending on its identity.
  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;
  });

  const [items, setItems] = useState<SmartPromptItem[] | null>(null);
  const [settledSignature, setSettledSignature] = useState<string | null>(null);
  const [holdExpired, setHoldExpired] = useState(false);

  // The context's subject, not its object identity — it is rebuilt freely by
  // its owner and would otherwise refetch on every render.
  const contextKey = describeClientContext(clientContext);
  const contextRef = useRef(clientContext);
  useEffect(() => {
    contextRef.current = clientContext;
  });

  const signature = `${isAuthenticated ? 'auth' : 'guest'}:${language}:${limit}:${contextKey}:${refreshKey}`;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const search = `?locale=${encodeURIComponent(language)}&limit=${limit}`;

    const load = async () => {
      try {
        // POST carries the guest context; GET stays the simple path, and is
        // what an older cached embed build calls.
        const context = contextRef.current;
        const response = context
          ? await queryRef.current('/api/prompts/suggested', {
              method: 'POST',
              body: JSON.stringify({ locale: language, limit, client_context: context }),
              headers: { 'Content-Type': 'application/json' },
            })
          : await queryRef.current(`/api/prompts/suggested${search}`, {
              method: 'GET',
            });
        const data: SmartPromptsResponse = await response.json();
        if (!cancelled) setItems(Array.isArray(data?.prompts) ? data.prompts : null);
      } catch (err) {
        if (cancelled) return;
        console.warn('Smart prompts fetch failed; using default chips:', err);
        setItems(null);
      } finally {
        if (!cancelled) setSettledSignature(signature);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [enabled, signature, language, limit]);

  // Release the caller's hold after MAX_HOLD_MS whatever the request is doing.
  useEffect(() => {
    if (!enabled) return undefined;
    setHoldExpired(false);
    const timer = setTimeout(() => setHoldExpired(true), MAX_HOLD_MS);
    return () => clearTimeout(timer);
  }, [enabled, signature]);

  const prompts = useMemo(() => {
    if (!items) return null;
    const rendered = renderSmartPrompts(items, t, i18n);
    // A response whose every item this build cannot render is no better than
    // no response; let the caller fall back rather than showing one chip.
    return rendered.length > 0 ? rendered : null;
  }, [items, t, i18n]);

  return {
    prompts,
    isLoadingFirstResult: enabled && settledSignature === null && !holdExpired,
  };
}
